/**
 * MLS Listings — landlord page to search the MLS feed and import
 * selected listings into the app's public listings collection.
 */
import { useState } from "react";
import { PageHeader, Card, Button, Input, Pill, Loader, EmptyState } from "@/components/ui/Primitives";
import { Search, Download, ExternalLink, Home, BedDouble, Bath } from "lucide-react";
import { toast } from "sonner";
import MlsSchema from "@/components/seo/MlsSchema";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

export default function MlsListings() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState({});

  async function search(e) {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const url = new URL(`${API}/api/mls/search`);
      if (q) url.searchParams.set("q", q);
      if (city) url.searchParams.set("city", city);
      if (province) url.searchParams.set("province", province);
      url.searchParams.set("limit", "24");
      const r = await fetch(url, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Search failed");
      setItems(d.items || []);
      if (!d.items?.length) toast.info("No MLS listings matched. Try broader filters.");
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function importListing(item) {
    setImporting((s) => ({ ...s, [item.mls_id]: true }));
    try {
      const r = await fetch(`${API}/api/mls/import`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ mls_id: item.mls_id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Import failed");
      toast.success(d.already_imported ? "Already imported" : "Imported to your listings");
    } catch (err) { toast.error(String(err.message)); }
    finally { setImporting((s) => ({ ...s, [item.mls_id]: false })); }
  }

  return (
    <div data-testid="mls-page">
      <MlsSchema />
      <PageHeader title="MLS Listings" subtitle="Search the MLS feed and import listings straight into your public feed." />

      <form onSubmit={search} className="mb-5 grid sm:grid-cols-4 gap-2">
        <Input placeholder="Keywords (e.g. condo, backyard)" value={q} onChange={(e) => setQ(e.target.value)} testid="mls-q"/>
        <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} testid="mls-city"/>
        <Input placeholder="Province (ON, QC, BC…)" value={province} onChange={(e) => setProvince(e.target.value)} testid="mls-province"/>
        <Button type="submit" disabled={busy} data-testid="mls-search-submit"><Search className="w-4 h-4 inline mr-1"/>{busy ? "Searching…" : "Search"}</Button>
      </form>

      {busy ? <Loader/> : items.length === 0 ? (
        <EmptyState icon={Home} title="No results yet" description="Run a search above to pull in your MLS listings."/>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <Card key={it.mls_id} className="p-4 flex flex-col gap-2" data-testid={`mls-item-${it.mls_id}`}>
              {it.photos?.[0] && <img src={typeof it.photos[0] === "string" ? it.photos[0] : it.photos[0].url} alt="" className="w-full h-32 object-cover rounded-lg"/>}
              <div className="font-bold text-slate-900 truncate">{it.address || it.title || `MLS ${it.mls_id}`}</div>
              <div className="text-xs text-slate-500">{it.city}{it.province ? `, ${it.province}` : ""}</div>
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1"><BedDouble className="w-3 h-3"/>{it.bedrooms || 0}</span>
                <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3"/>{it.bathrooms || 0}</span>
                <Pill tone="slate">MLS {it.mls_id}</Pill>
              </div>
              {it.price ? <div className="font-display text-lg font-extrabold text-emerald-700 tabular-nums">${Number(it.price).toLocaleString()}</div> : null}
              <p className="text-xs text-slate-600 line-clamp-2">{it.description}</p>
              <div className="flex items-center justify-between pt-1 mt-auto">
                <Button
                  variant="secondary"
                  onClick={() => importListing(it)}
                  disabled={!!importing[it.mls_id]}
                  className="!py-1.5 !px-3 text-xs"
                  data-testid={`mls-import-${it.mls_id}`}
                >
                  <Download className="w-3 h-3 inline mr-1"/>{importing[it.mls_id] ? "Importing…" : "Import to my listings"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
